import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";

interface StandardStep {
  type: 'partial' | 'final';
  description: string;
  digit?: number;
  product?: number;
  ones?: number;
  carry?: number;
  position?: number;
  result?: number;
}

const MethodSimulation = () => {
  const [num1, setNum1] = useState(23);
  const [num2, setNum2] = useState(45);
  const [russianStep, setRussianStep] = useState(0);

  const [standardSteps, setStandardSteps] = useState<any[]>([]);
  const [currentStandardStep, setCurrentStandardStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  // Generate steps for standard multiplication with proper typing
  const generateStandardSteps = (a: number, b: number): StandardStep[] => {
    const bStr = b.toString();
    const steps = [];
    let carry = 0;
    let result = 0;
    let partialResults = [];
    
    // Generate partial products
    for (let i = bStr.length - 1; i >= 0; i--) {
      const digit = parseInt(bStr[i]);
      let product = a * digit + carry;
      let ones = product % 10;
      carry = Math.floor(product / 10);
      
      partialResults.unshift({
        digit,
        product,
        ones,
        carry,
        position: bStr.length - 1 - i,
        partialResult: product * Math.pow(10, bStr.length - 1 - i)
      });
      
      steps.push({
        type: 'partial',
        digit,
        product,
        ones,
        carry,
        position: bStr.length - 1 - i,
        partialResults: [...partialResults],
        description: `${a} × ${digit} = ${product} (${ones} simpan ${carry})`
      });
    }
    
    // Add final result
    if (carry > 0) {
      steps.push({
        type: 'final',
        result: a * b,
        description: `Hasil akhir: ${a} × ${b} = ${a * b}`
      });
    }
    
    return steps;
  };

  const latticeMethod = () => {
    const n1Str = num1.toString();
    const n2Str = num2.toString();
    const grid = [];
    
    for (let i = 0; i < n1Str.length; i++) {
      grid[i] = [];
      for (let j = 0; j < n2Str.length; j++) {
        const product = parseInt(n1Str[i]) * parseInt(n2Str[j]);
        grid[i][j] = {
          tens: Math.floor(product / 10),
          ones: product % 10
        };
      }
    }
    return grid;
  };

  const russianMethod = () => {
    const steps = [];
    let a = num1;
    let b = num2;
    let result = 0;

    while (a > 0) {
      if (a % 2 === 1) {
        result += b;
        steps.push({ a, b, add: true, result });
      } else {
        steps.push({ a, b, add: false, result });
      }
      a = Math.floor(a / 2);
      b = b * 2;
    }
    return steps;
  };

  const grid = latticeMethod();
  const russianSteps = russianMethod();
  
  // Move StandardStep interface to the top

  // Update standard steps when numbers change
  useEffect(() => {
    const steps = generateStandardSteps(num1, num2);
    setStandardSteps(steps);
    setCurrentStandardStep(0);
  }, [num1, num2]);
  
  // Get current step with proper typing
  const currentStep: StandardStep = standardSteps[currentStandardStep] || {
    type: 'partial',
    description: 'Mulai perkalian...',
    digit: 0,
    product: 0,
    ones: 0,
    carry: 0,
    position: 0
  };

  const handleNextStep = () => {
    if (currentStandardStep < standardSteps.length - 1) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStandardStep(prev => prev + 1);
        setIsAnimating(false);
      }, 300);
    }
  };

  const handlePrevStep = () => {
    if (currentStandardStep > 0) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStandardStep(prev => prev - 1);
        setIsAnimating(false);
      }, 300);
    }
  };

  // StandardStep interface is now at the top of the file

  return (
    <Tabs defaultValue="standard" className="w-full" onValueChange={() => setRussianStep(0)}>
      <div className="overflow-x-auto pb-1">
        <TabsList className="w-full grid grid-cols-4 min-w-[400px]">
          <TabsTrigger 
            value="standard" 
            className="text-xs sm:text-sm px-2 py-2 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600 data-[state=active]:shadow-sm data-[state=active]:border-b-2 data-[state=active]:border-blue-500"
          >
            Standar
          </TabsTrigger>
          <TabsTrigger 
            value="lattice" 
            className="text-xs sm:text-sm px-2 py-2 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600 data-[state=active]:shadow-sm data-[state=active]:border-b-2 data-[state=active]:border-blue-500"
          >
            Lattice
          </TabsTrigger>
          <TabsTrigger 
            value="russian" 
            className="text-xs sm:text-sm px-2 py-2 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600 data-[state=active]:shadow-sm data-[state=active]:border-b-2 data-[state=active]:border-blue-500"
          >
            Rusia
          </TabsTrigger>
          <TabsTrigger 
            value="visual" 
            className="text-xs sm:text-sm px-2 py-2 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600 data-[state=active]:shadow-sm data-[state=active]:border-b-2 data-[state=active]:border-blue-500"
          >
            Visual Grid
          </TabsTrigger>
        </TabsList>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium">Angka 1:</label>
          <Input
            type="number"
            value={num1}
            onChange={(e) => setNum1(parseInt(e.target.value) || 0)}
          />
        </div>
        <div>
          <label className="text-sm font-medium">Angka 2:</label>
          <Input
            type="number"
            value={num2}
            onChange={(e) => setNum2(parseInt(e.target.value) || 0)}
          />
        </div>
      </div>

      <TabsContent value="standard" className="mt-6">
        <Card>
          <CardHeader>
            <CardTitle>Metode Standar</CardTitle>
            <CardDescription>
              Visualisasi perkalian menggunakan metode standar dengan animasi langkah per langkah
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="text-center text-xl font-mono bg-gray-50 p-4 rounded-lg">
                <div className="text-sm text-gray-500 mb-2">Perkalian: {num1} × {num2}</div>
                <div className="text-2xl font-bold">
                  <div key={currentStandardStep} className="inline-block transition-opacity duration-300">
                {currentStep?.description || 'Mulai perkalian...'}
              </div>
                </div>
              </div>

              {currentStep.type === 'partial' && (
                <div className="space-y-2">
                  <div className="text-sm font-medium text-gray-700">Langkah {currentStandardStep + 1}:</div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="font-mono text-center text-lg">
                      {num1} × {currentStep.digit} = {currentStep.product}
                    </div>
                    <div className="mt-2 text-sm text-gray-600">
                      <p>• Satuan: {currentStep.ones}</p>
                      <p>• Simpanan: {currentStep.carry || '0'}</p>
                      <p>• Posisi: 10<sup>{currentStep.position}</sup> (geser {currentStep.position} digit ke kiri)</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-between mt-6">
                <Button 
                  onClick={handlePrevStep} 
                  disabled={currentStandardStep === 0 || isAnimating}
                  variant="outline"
                >
                  Sebelumnya
                </Button>
                <Button 
                  onClick={handleNextStep} 
                  disabled={currentStandardStep >= standardSteps.length - 1 || isAnimating}
                >
                  {currentStandardStep < standardSteps.length - 1 ? 'Langkah Selanjutnya' : 'Selesai'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="lattice" className="mt-6">
        <Card>
          <CardHeader>
            <CardTitle>Metode Lattice (Kisi-kisi)</CardTitle>
            <CardDescription>
              Visualisasi perkalian menggunakan metode kisi-kisi
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center space-y-4">
              <div className="text-lg font-bold">
                {num1} × {num2} = {num1 * num2}
              </div>
              
              <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${num2.toString().length}, 1fr)` }}>
                {grid.map((row, i) => 
                  row.map((cell, j) => (
                    <div
                      key={`${i}-${j}`}
                      className="w-16 h-16 border-2 border-border flex flex-col justify-center items-center text-sm relative"
                    >
                      <div className="absolute top-1 left-1 text-xs text-muted-foreground">
                        {cell.tens}
                      </div>
                      <div className="absolute bottom-1 right-1 text-xs text-muted-foreground">
                        {cell.ones}
                      </div>
                      <div className="absolute inset-0 border-l border-t border-muted-foreground transform rotate-45 origin-center"></div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="russian" className="mt-6">
        <Card>
          <CardHeader>
            <CardTitle>Perkalian Petani Rusia</CardTitle>
            <CardDescription>
              Metode perkalian dengan pembagian dan penggandaan
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="text-lg font-bold text-center">
                {num1} × {num2} = {num1 * num2}
              </div>
              
              <div className="grid grid-cols-4 gap-2 text-sm">
                <div className="font-bold">A</div>
                <div className="font-bold">B</div>
                <div className="font-bold">Ganjil?</div>
                <div className="font-bold">Jumlah</div>
                
                {russianSteps.map((step, index) => (
                  <>
                    <div key={`a-${index}`} className="p-2 bg-muted rounded">
                      {step.a}
                    </div>
                    <div key={`b-${index}`} className="p-2 bg-muted rounded">
                      {step.b}
                    </div>
                    <div key={`add-${index}`} className={`p-2 rounded ${step.add ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                      {step.add ? '✓' : '✗'}
                    </div>
                    <div key={`result-${index}`} className="p-2 bg-accent rounded">
                      {step.result}
                    </div>
                  </>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="visual" className="mt-6">
        <Card>
          <CardHeader>
            <CardTitle>Grid Visual</CardTitle>
            <CardDescription>
              Representasi visual perkalian sebagai area persegi panjang
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center space-y-4">
              <div className="text-lg font-bold">
                {num1} × {num2} = Area {num1 * num2} kotak
              </div>
              
              <div 
                className="grid gap-0.5 border-2 border-primary p-2"
                style={{ 
                  gridTemplateColumns: `repeat(${Math.min(num2, 20)}, 1fr)`,
                  maxWidth: '400px'
                }}
              >
                {Array.from({ length: Math.min(num1 * num2, 400) }, (_, index) => (
                  <div
                    key={index}
                    className="w-4 h-4 bg-primary/20 border border-primary/40"
                  />
                ))}
              </div>
              
              {num1 * num2 > 400 && (
                <p className="text-sm text-muted-foreground">
                  Menampilkan 400 kotak pertama dari total {num1 * num2} kotak
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
};

export default MethodSimulation;